import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { BsCloudUpload, BsFileEarmarkExcel } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useImportBooks from "api/BookResource/useImportBooks";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";

const UploadStep = ({ onBack, onSuccess }) => {
	const [selectedFile, setSelectedFile] = useState(null);
	const { mutateAsync, isLoading } = useImportBooks();

	const handleImport = async () => {
		if (!selectedFile) return;
		try {
			const result = await mutateAsync({ csv: selectedFile });
			if (result?.downloadUrl) {
				onSuccess(result);
			}
		} catch {
			// Errors toasted in useImportBooks
		}
	};

	const onDrop = useCallback((accepted) => {
		if (accepted?.[0]) {
			setSelectedFile(accepted[0]);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop,
		multiple: false,
		noClick: true,
		accept: {
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
			"application/vnd.ms-excel": [".xls"],
			"text/csv": [".csv"],
		},
		disabled: isLoading,
	});

	return (
		<StepPanel
			stepNumber={2}
			title="Upload your file"
			description="Select an Excel (.xlsx, .xls) or CSV file with book metadata. Processing runs on the server and returns a result spreadsheet."
			status={
				selectedFile ? (
					<StatusBanner tone="success" title="File ready">
						{selectedFile.name} (
						{(selectedFile.size / 1024).toFixed(1)} KB) — click Import
						books to continue.
					</StatusBanner>
				) : (
					<StatusBanner tone="warning" title="File required">
						Choose a spreadsheet before importing.
					</StatusBanner>
				)
			}
			nav={{
				onBack,
				onNext: handleImport,
				backLabel: "← Back to prepare",
				nextLabel: "Import books",
				nextDisabled: !selectedFile,
				nextDisabledReason: selectedFile
					? null
					: "Select a file before importing.",
				busy: isLoading,
				busyLabel: "Processing…",
			}}
		>
			<div className="space-y-4">
				<div
					{...getRootProps()}
					className={`rounded-2xl border-2 border-dashed p-8 text-center transition-all duration-200 ${
						isDragActive
							? "border-akgreen bg-green-50"
							: "border-gray-200 bg-gray-50 hover:border-akgreen"
					}`}
				>
					<input {...getInputProps()} />
					<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-akgreen shadow-sm">
						<BsCloudUpload className="h-6 w-6" aria-hidden />
					</div>
					<p className="text-sm font-medium text-gray-900">
						{isDragActive
							? "Drop your file here…"
							: "Drag & drop your spreadsheet, or browse"}
					</p>
					<p className="mt-1 text-xs text-gray-500">
						Accepted: .xlsx, .xls, .csv
					</p>
					<div className="mt-3 flex items-center justify-center gap-2">
						<span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-akgreen">
							XLSX
						</span>
						<span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
							XLS
						</span>
						<span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
							CSV
						</span>
					</div>
					<button
						type="button"
						className={`mt-4 rounded-lg border border-akgreen bg-white px-4 py-2 text-sm font-medium text-akgreenest transition hover:bg-gray-50 ${
							isLoading ? "cursor-not-allowed opacity-50" : ""
						}`}
						disabled={isLoading}
						onClick={open}
					>
						{selectedFile ? "Replace file" : "Choose file"}
					</button>
				</div>

				{selectedFile ? (
					<div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
						<span
							className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 text-akgreenest"
							aria-hidden
						>
							<BsFileEarmarkExcel className="h-5 w-5" />
						</span>
						<div className="min-w-0 flex-1">
							<p className="truncate text-sm font-medium text-gray-900">
								{selectedFile.name}
							</p>
							<p className="mt-0.5 text-xs text-gray-500">
								{(selectedFile.size / 1024).toFixed(1)} KB
								{isLoading ? " · Processing…" : " · Ready to import"}
							</p>
							{isLoading ? (
								<div className="mt-1.5 flex items-center gap-2 text-xs text-akgreener">
									<AiOutlineLoading3Quarters
										className="h-3.5 w-3.5 animate-spin"
										aria-hidden
									/>
									Importing books…
								</div>
							) : null}
						</div>
						{!isLoading ? (
							<button
								type="button"
								className="flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
								onClick={() => setSelectedFile(null)}
							>
								Clear
							</button>
						) : null}
					</div>
				) : null}
			</div>
		</StepPanel>
	);
};

export default UploadStep;
